import { cva, type VariantProps } from 'class-variance-authority';
import { Dialog as DialogPrimitive } from 'radix-ui';
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  type ComponentPropsWithoutRef,
  type ReactNode,
  type RefObject,
} from 'react';

import { cn } from '@/lib/cn';

/**
 * Dialog — the modal surface (`FE-04.10`).
 *
 * Asked for by `card-back.md` (the card back shell, the delete-label confirm,
 * the delete-comment confirm) and by `entry-surfaces.md` (workspace creation
 * from the header menu, the search field below 640px).
 *
 * ── The two things B3-BRIEF §2 says must not be dropped ────────────────────
 *
 * 1. **The panel is opaque `--color-surface`, never `--color-panel`.** The card
 *    back is wall-to-wall `--color-ink-muted` (author, relative timestamp,
 *    "(edited)", counters) and muted text needs a 98% panel against the panel
 *    token's measured 92% floor. `--color-surface` — not `--color-surface-raised`
 *    — so that a `Notice`, a composer or a picker row can sit *on* the modal in
 *    `--color-surface-raised` and read as raised. Inside the modal, the modal is
 *    ground (card back D3).
 *
 * 2. **The 1px `--color-border-strong` edge is mandatory and permanent, in both
 *    themes and every size.** The scrim (`bg-overlay`) measures 3.89:1 against
 *    the scrimmed board in light theme and **1.13:1** in dark, because the
 *    default board is already near-black and darkening it is a no-op. The border
 *    is the modal's only boundary in dark theme, at 3.53:1. It is not a variant
 *    and it is not decoration — see the `--color-overlay` comment in
 *    `styles.css`, which measures all of it.
 *
 * The scrim itself is `bg-overlay` and nothing else. Do not write a second one.
 */

const dialogPanelVariants = cva(
  // `border-border-strong` is in the BASE, not in a variant, deliberately: see
  // point 2 above. `--color-border` (decorative, 1.30:1) is used for the rules
  // *inside* the panel and never for its edge.
  //
  // `overflow-clip`, never `overflow-hidden`: a hidden box is still a scroll
  // container, and `sr-only` descendants of the body (absolute, so contained by
  // this fixed panel rather than the body scroller) give it ~60px of overflow.
  // A comment deep link's `scrollIntoView` then scrolled the PANEL and hid the
  // title and close control under its top edge. A clipped box cannot scroll.
  'fixed top-1/2 left-1/2 z-50 flex max-h-[min(90vh,46rem)] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-clip rounded-card border border-border-strong bg-surface text-ink shadow-card',
  {
    variants: {
      size: {
        // `confirm` in card-back's spelling: "Delete this label?" and its two
        // buttons.
        sm: 'max-w-[26rem]',
        // entry-surfaces' default: the workspace-creation form.
        md: 'max-w-[34rem]',
        // card-back's 720px shell (D1: single column, 664px content in a 720px
        // modal).
        lg: 'max-w-[45rem]',
      },
    },
    defaultVariants: { size: 'md' },
  },
);

/* ───────────────────────── the dismissal ladder ─────────────────────────── */

/**
 * Card back D2 — the dismissal ladder, fixed: popover → inline editor → modal.
 *
 * `FR-CARD-001` AC2 says Escape closes the card back; `FR-CARD-002` says Escape
 * reverts the title edit. Composed naively the modal wins and the member's
 * typing is gone, silently.
 *
 * **Level 1 is free and must not be re-implemented.** Radix's `DismissableLayer`
 * registers its Escape handler only when it is the highest layer
 * (`react-dismissable-layer@1.1.19`, the `isHighestLayer` guard), so an open
 * `Popover` inside an open `Dialog` consumes the keystroke by itself. `Popover`
 * in this directory therefore does **not** register here — double-handling a
 * `pointerDownOutside` is how a click that should dismiss the dialog gets
 * swallowed instead.
 *
 * **Level 2 is not free.** That same listener is attached to the document with
 * `{ capture: true }`, so an inline editor calling `stopPropagation()` on its
 * own `keydown` cannot reach it — the capture phase has already run. A
 * non-layer capturer has to say so out of band, which is what this context is.
 *
 * ⚠ **`InlineEdit` is the intended caller and does not call this yet.** It ships
 * in this same chunk and defends itself with `event.stopPropagation()` plus
 * `event.nativeEvent.stopImmediatePropagation()`, which cannot work: Radix's
 * listener is on the **document, in the capture phase**, so it has already run
 * and already dismissed by the time any handler on the input is reached.
 * Measured, not predicted — an `InlineEdit` inside this `Dialog` loses the
 * member's typing *and* closes the card back on one Escape. The fix is one line
 * inside `InlineEdit`: `useDismissalCapture(status === 'editing')`. Reported in
 * the chunk return.
 */
interface DismissalCaptureContextValue {
  register: (key: string) => void;
  unregister: (key: string) => void;
}

const DismissalCaptureContext = createContext<DismissalCaptureContextValue | null>(null);

/**
 * Register the calling component as capturing dismissal while `active`.
 *
 * While at least one descendant is capturing, the enclosing `Dialog` swallows
 * Escape and outside-pointer-down instead of closing. A no-op outside a
 * `Dialog`, so a component that may or may not be inside one calls it
 * unconditionally.
 */
export function useDismissalCapture(active: boolean): void {
  const context = useContext(DismissalCaptureContext);
  const key = useId();

  useEffect(() => {
    if (!active || !context) return;
    context.register(key);
    return () => context.unregister(key);
  }, [active, context, key]);
}

/* ─────────────────────────────── Dialog ─────────────────────────────────── */

export interface DialogProps
  extends
    VariantProps<typeof dialogPanelVariants>,
    Omit<ComponentPropsWithoutRef<'div'>, 'title'> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Rendered in the header **and** used as the accessible name.
   *
   * A `ReactNode` rather than a string because card back's title is an editable
   * live node: passing the editor here keeps the accessible name in step with
   * what is on screen, which is what its `labelledBy` prop asked for by another
   * route. A caller must not render its own heading inside this node.
   */
  title: ReactNode;
  /** Optional supporting line. Becomes `aria-describedby`. */
  description?: ReactNode;
  /**
   * Header content that is part of NEITHER the accessible name nor the
   * description — rendered in the header, under `title` and `description`.
   *
   * ⚠ It exists because a control placed inside `title` becomes part of the
   * dialog's accessible name: the card back's reference and its Copy button
   * (`docs/notes/card-keys.md`) would have read "<title> MG-0001 Copy
   * reference" to every screen reader on open, and moved the name every test
   * locates the card back by.
   */
  headerAside?: ReactNode;
  /**
   * Controls that sit in the header row beside the close control — the card
   * back's "⋯" Actions menu (`02-card-detail.md`'s layout sentence: "an
   * Actions menu in the top right").
   *
   * ⚠ A separate slot because neither neighbour can hold a menu trigger:
   * `title` would fold its name into the dialog's accessible name, and
   * `closeControl` is wrapped in `Dialog.Close`, so any click on it would
   * close the dialog. Rendered just before `closeControl`, outside both.
   */
  headerActions?: ReactNode;
  /**
   * The close control, as a slot (B3-BRIEF §9). The caller passes
   * `<IconButton label="Close" variant="quiet" size="sm" />`; the primitive owns
   * the *behaviour* by wrapping it in Radix's `Dialog.Close`, so no caller wires
   * `onOpenChange(false)` by hand.
   */
  closeControl?: ReactNode;
  /** Sticky footer. Confirm dialogs put Cancel and the destructive action here. */
  footer?: ReactNode;
  /**
   * Focused on open instead of the panel itself. Radix otherwise focuses the
   * content container, which draws the global focus ring around the whole modal
   * — and `outline-none` is not the fix (B3-BRIEF §3).
   */
  initialFocus?: RefObject<HTMLElement | null>;
  /** Focused on close instead of the element that opened the dialog. */
  returnFocus?: RefObject<HTMLElement | null>;
  children: ReactNode;
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  headerAside,
  headerActions,
  closeControl,
  footer,
  initialFocus,
  returnFocus,
  size,
  className,
  children,
  ...rest
}: DialogProps) {
  const descriptionId = useId();
  const capturers = useRef<Set<string>>(new Set());

  const capture = useMemo<DismissalCaptureContextValue>(
    () => ({
      register: (key) => capturers.current.add(key),
      unregister: (key) => capturers.current.delete(key),
    }),
    [],
  );

  /** True while a descendant (an inline editor) owns the next dismissal. */
  const isCaptured = () => capturers.current.size > 0;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        {/* The one sanctioned scrim. `bg-overlay` — never a hand-rolled mix. */}
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-overlay" />
        <DialogPrimitive.Content
          {...rest}
          aria-describedby={description ? descriptionId : undefined}
          className={cn(dialogPanelVariants({ size }), className)}
          onEscapeKeyDown={(event) => {
            if (isCaptured()) event.preventDefault();
          }}
          onPointerDownOutside={(event) => {
            if (isCaptured()) event.preventDefault();
          }}
          onOpenAutoFocus={(event) => {
            if (!initialFocus?.current) return;
            event.preventDefault();
            initialFocus.current.focus();
          }}
          onCloseAutoFocus={(event) => {
            if (!returnFocus?.current) return;
            event.preventDefault();
            returnFocus.current.focus();
          }}
        >
          <DismissalCaptureContext.Provider value={capture}>
            {/*
              Sticky, not scrolled away: `FR-CARD-034` puts an unbounded comment
              stream in the same scroller, and `FR-CARD-001` AC2's close control
              must stay reachable without scrolling back up (card back D4).
            */}
            <header className="flex shrink-0 items-start gap-3 border-b border-border px-5 py-4">
              <div className="min-w-0 flex-1">
                <DialogPrimitive.Title className="font-display text-xl leading-tight text-ink">
                  {title}
                </DialogPrimitive.Title>
                {/*
                  One line under the title: the description, then the aside
                  beside it, wrapping on a narrow panel. Without an aside the
                  markup is exactly what it always was.
                */}
                {headerAside === undefined ? (
                  description ? (
                    <DialogPrimitive.Description
                      id={descriptionId}
                      className="mt-1 text-sm text-ink-muted"
                    >
                      {description}
                    </DialogPrimitive.Description>
                  ) : null
                ) : (
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                    {description ? (
                      <DialogPrimitive.Description
                        id={descriptionId}
                        className="text-sm text-ink-muted"
                      >
                        {description}
                      </DialogPrimitive.Description>
                    ) : null}
                    {headerAside}
                  </div>
                )}
              </div>
              {headerActions === undefined ? null : (
                <div className="flex shrink-0 items-center gap-1">{headerActions}</div>
              )}
              {closeControl ? (
                <DialogPrimitive.Close asChild>{closeControl}</DialogPrimitive.Close>
              ) : null}
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

            {footer ? (
              <footer className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border px-5 py-3">
                {footer}
              </footer>
            ) : null}
          </DismissalCaptureContext.Provider>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
